'use client';

import { ListIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/list-icon';
import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Input } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/input';
import type { SmartLinkOption } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseSmartLinkDropdownProps {
  getOptions?: () => Promise<SmartLinkOption[]>;
  onSelect: (option: SmartLinkOption) => void;
}

export function useSmartLinkDropdown({ getOptions, onSelect }: UseSmartLinkDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<SmartLinkOption[]>([]);
  const [filter, setFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const enabled = !!getOptions;

  const filteredOptions = useMemo(
    () => (filter ? options.filter((opt) => opt.label.toLowerCase().includes(filter.toLowerCase())) : options),
    [options, filter],
  );

  const loadOptions = useCallback(async () => {
    if (!getOptions) return;
    setIsLoading(true);
    try {
      const result = await getOptions();
      setOptions(result);
    } catch {
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [getOptions]);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    if (!enabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      setFilter('');
      setHighlightedIndex(-1);
      loadOptions();
    }
  }, [enabled, isOpen, loadOptions]);

  const selectOption = useCallback(
    (option: SmartLinkOption) => {
      onSelect(option);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleFilterKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const count = filteredOptions.length;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < count) {
            selectOption(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          close();
          break;
      }
    },
    [filteredOptions, highlightedIndex, selectOption, close],
  );

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filter]);

  return {
    enabled,
    isOpen,
    isLoading,
    filter,
    setFilter,
    filteredOptions,
    highlightedIndex,
    toggle,
    close,
    selectOption,
    handleFilterKeyDown,
  };
}

export function SmartLinkToggleButton({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  return (
    <Button
      type="button"
      onClick={onToggle}
      title="Browse links"
      data-style="ghost"
      data-active-state={isOpen ? 'on' : 'off'}
    >
      <ListIcon className="tiptap-button-icon" />
    </Button>
  );
}

export interface SmartLinkDropdownPanelProps {
  isLoading: boolean;
  filter: string;
  setFilter: (value: string) => void;
  filteredOptions: SmartLinkOption[];
  highlightedIndex: number;
  onSelect: (option: SmartLinkOption) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}

export function SmartLinkDropdownPanel({
  isLoading,
  filter,
  setFilter,
  filteredOptions,
  highlightedIndex,
  onSelect,
  onKeyDown,
}: SmartLinkDropdownPanelProps) {
  const filterInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    filterInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (highlightedIndex < 0 || !optionsRef.current) return;
    const highlighted = optionsRef.current.children[highlightedIndex] as HTMLElement | undefined;
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  return (
    <div className="tiptap-smart-link-dropdown" onClick={(e) => e.stopPropagation()}>
      <Input
        ref={filterInputRef}
        type="text"
        placeholder="Search..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        onKeyDown={onKeyDown}
        className="tiptap-smart-link-filter"
      />

      <div ref={optionsRef} className="tiptap-smart-link-options" role="listbox">
        {isLoading ? (
          <div className="tiptap-smart-link-empty">Loading...</div>
        ) : filteredOptions.length === 0 ? (
          <div className="tiptap-smart-link-empty">No matching options</div>
        ) : (
          filteredOptions.map((option, index) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={index === highlightedIndex}
              className={`tiptap-smart-link-option${index === highlightedIndex ? ' tiptap-smart-link-option--highlighted' : ''}`}
              onClick={() => onSelect(option)}
            >
              {option.label}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
